import { isDefined } from '@evorait/pace-core';
import { CHECKPOINTS_STATUS } from '../types/checkpoints-status';
import { Choice, CHOICE_TYPE, Choices } from '../types/choice';
import { FormIdentifier } from '../types/form-identifier';
import { FormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../types/functional-element';
import { FunctionalElements } from '../types/functional-elements';
import { CosimaOption, isCosimaOption } from '../types/option';
import { Signature } from '../types/signature';

const docLanguageLength = 2;

export namespace FormUtilities {
    export const compare = (first: FormIdentifier, second: FormIdentifier): boolean =>
        first.equipmentId === second.equipmentId &&
        first.formId === second.formId &&
        first.materialId === second.materialId;

    export const toString = (id: FormIdentifier, separator = '-'): string =>
        `Form${separator}${id.equipmentId}${separator}${id.materialId}${separator}${id.formId}`;

    export const getFormTypeFromId = (formId: string): FormsConstants.FORMTYPE => {
        const matches = formId.match(/[^.]+\.([0-9]{3})\..*/);
        if (!matches) {
            return FormsConstants.FORMTYPE.UNKNOWN;
        }
        const type = matches[1];
        if (!Object.values(FormsConstants.FORMTYPE).includes(type as FormsConstants.FORMTYPE)) {
            return FormsConstants.FORMTYPE.UNKNOWN;
        }

        return type as FormsConstants.FORMTYPE;
    };

    export const getFormLanguage = (formId: string): FormsConstants.FORM_LANGUAGES => {
        const languageIdentifier = formId.slice(-docLanguageLength) as FormsConstants.FORM_LANGUAGES;
        return languageIdentifier;
    };

    export const getFormBaseDocumentId = (formId: string): string => {
        const baseDocumentId = formId.slice(0, -docLanguageLength);
        return baseDocumentId;
    };

    export const isNewFormVersion = (formId: string, formIdToTest: string): boolean => {
        const versionCaptureRegex = /([^.]+\.[0-9]{3}\.[0-9]*\.)([0-9]+)(.*)/;
        const FULL_MATCH = 0;
        const BEFORE_VERSION = 1;
        const VERSION = 2;
        const AFTER_VERSION = 3;

        const oldMatches = formId.match(versionCaptureRegex);
        const newMatches = formIdToTest.match(versionCaptureRegex);

        if (!isDefined(oldMatches) || !isDefined(newMatches)) {
            return false;
        }

        const isSameForm =
            oldMatches[FULL_MATCH] === newMatches[FULL_MATCH] ||
            (oldMatches[BEFORE_VERSION] === newMatches[BEFORE_VERSION] &&
                oldMatches[AFTER_VERSION] === newMatches[AFTER_VERSION]);

        return isSameForm && oldMatches[VERSION] !== newMatches[VERSION];
    };

    export const isAstoriaForm = (formType: FormsConstants.FORMTYPE): boolean =>
        FormsConstants.ASTORIA_FORMTYPES.includes(formType);
    export const isCosimaForm = (formType: FormsConstants.FORMTYPE): boolean =>
        FormsConstants.COSIMA_FORMTYPES.includes(formType);

    export const isServicePartQuestionnaire = (formType: FormsConstants.FORMTYPE): boolean =>
        formType === FormsConstants.FORMTYPE.REPLACEMENT_REPORT;

    // Based on a list provided by Christian Gebert on 2020-03-19.
    // Values need to be checked again when migrating to Cosima.
    export const getElementType = (elementType: string): FormsConstants.ELEMENT_TYPE => {
        switch (elementType) {
            case 'A':
                return FormsConstants.ELEMENT_TYPE.A;
            case 'B':
                return FormsConstants.ELEMENT_TYPE.B;
            case 'C':
                return FormsConstants.ELEMENT_TYPE.C;
            case 'D':
                return FormsConstants.ELEMENT_TYPE.D;
            case 'E':
                return FormsConstants.ELEMENT_TYPE.E;
            case 'F':
                return FormsConstants.ELEMENT_TYPE.F;
            case '-':
            case 'PM':
            case 'PMA':
            case 'PMF':
            case 'PMP':
                return FormsConstants.ELEMENT_TYPE.STANDARD;
            case 'Q':
            case 'QIQ':
            case 'QSQ':
                return FormsConstants.ELEMENT_TYPE.QUALITY;
            case 'S':
            case 'SI':
            case 'SIE':
            case 'SIM':
                return FormsConstants.ELEMENT_TYPE.SAFETY;
            case 'SW':
            case 'P':
                return FormsConstants.ELEMENT_TYPE.SOFTWARE;
            case 'T':
                return FormsConstants.ELEMENT_TYPE.TIME;
            case 'M':
                return FormsConstants.ELEMENT_TYPE.MATERIAL;
            default:
                return FormsConstants.ELEMENT_TYPE.UNKNOWN;
        }
    };

    export const getFilterLabel = (elementType: FormsConstants.ELEMENT_TYPE, filters: FormTypeFilters): string => {
        // type A-F are used in training checklists for APPS, where the labels should not be displayed.
        return [
            FormsConstants.ELEMENT_TYPE.A,
            FormsConstants.ELEMENT_TYPE.B,
            FormsConstants.ELEMENT_TYPE.C,
            FormsConstants.ELEMENT_TYPE.D,
            FormsConstants.ELEMENT_TYPE.E,
            FormsConstants.ELEMENT_TYPE.F,
        ].includes(elementType)
            ? ''
            : filters[elementType].label;
    };

    export const isSignatureValid = (signature: Signature): boolean => {
        return (
            (signature.firstName.length > 0 &&
                signature.lastName.length > 0 &&
                signature.signatureDataPoints.length > 0) ||
            (signature.firstName === FormsConstants.NA && signature.lastName === FormsConstants.NA)
        );
    };

    export const isFormUnlocked = (submitStatus: FormsConstants.SUBMIT_STATUS): boolean => {
        return (
            submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM ||
            submitStatus === FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK ||
            submitStatus === FormsConstants.SUBMIT_STATUS.HANDOVER ||
            submitStatus === FormsConstants.SUBMIT_STATUS.FINAL ||
            submitStatus === FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK ||
            submitStatus === FormsConstants.SUBMIT_STATUS.ABORTED
        );
    };

    export const isSafetyAssessmentForm = (formId: string, gwFormId: string): boolean => {
        return formId === gwFormId;
    };

    export const isInstallationReport = (
        formType: FormsConstants.FORMTYPE,
        infoClass: FormsConstants.INFOCLASS,
    ): boolean => {
        return (
            (formType === FormsConstants.FORMTYPE.TRAININGCHECKLIST &&
                infoClass === FormsConstants.INFOCLASS.INSTALLATION) ||
            FormsConstants.COSIMA_INSTALLATION_FORMTYPES.includes(formType)
        );
    };

    export const checkpointsContainNotOkAnswers = (checkpoints: CHECKPOINTS_STATUS[]): boolean => {
        const containsNotOk =
            checkpoints.findIndex(
                (checkpointStatus) => checkpointStatus === CHECKPOINTS_STATUS.COMPLETELY_FILLED_NOT_OK,
            ) > -1;

        return containsNotOk;
    };

    export const isCosimaSignatureValid = (signature: Signature | undefined): boolean => {
        const signatureChoices = signature?.choices ? Object.values(signature.choices) : [];
        const choicesValid =
            signatureChoices.length > 0 ? signatureChoices.filter(isDefined).some((c) => c.selected) : true;

        const signatureValid =
            !isDefined(signature) ||
            (signature.firstName.length > 0 &&
                signature.lastName.length > 0 &&
                (signature.signature.length > 0 ||
                    (signature.firstName === FormsConstants.NA && signature.lastName === FormsConstants.NA)) &&
                choicesValid);

        return signatureValid;
    };

    export const isRemarksFieldHidden = (functionalElement: FunctionalElement): boolean => {
        return (
            !FormUtilities.isNegativeChoiceSelectedForFunctionalElement(functionalElement) &&
            (functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.STANDARD ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.DATE ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.VALUE ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.COMPONENT ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS) &&
            functionalElement.remarks?.length === 0
        );
    };

    export const isNegativeChoiceSelectedForFunctionalElement = (functionalElement: FunctionalElement): boolean => {
        const choices = functionalElement.choices;
        if (!isDefined(choices)) {
            throw new Error(
                `FormEditEffects: isNegativeChoiceSelectedForFunctionalElement: encountered functional element without choices!`,
            );
        }
        const selectedChoice = Object.values(choices).find((choice) => choice?.selected);

        return isDefined(selectedChoice) && selectedChoice.type === CHOICE_TYPE.CHOICE_NEGATIVE;
    };

    /**
     * Find the next "option present" element in a sequence which was created due to the occurrence attribute having a value
     * higher than 1.
     *
     * Note: The occurrence attribute is already evaluated in the publication process of the form by CSX, we
     * only receive the result and therefore have to live with the XML structure as it is.
     * The elements in such a sequence share an xml id with a common prefix and number suffix, separated by an underscore.
     *
     * Beware: The first element has no number suffix, the second element starts the suffix with a 2.
     *
     * Example:
     *
     * Three option elements with xml ids d1e1197_option, d1e1197_option_2, d1e1197_option_3
     * - If d1e1197_option was selected, then the next element would be d1e1197_option_2.
     * - If d1e1197_option_2 was selected, then we want to find d1e1197_option_3.
     * - If d1e1197_option_3 was selected, there is no remaining sibling and we return undefined.
     *
     * @param optionPresentElement the current option present element
     * @param functionalElements the functional elements from the form
     * @returns {(CosimaOption|undefined)} returns the found cosima option or undefined in case there is no option available
     * @throws error if the optionPresent does not have a xmlId
     */
    export const getNextOptionPresentSibling = (
        optionPresentElement: FunctionalElement,
        functionalElements: FunctionalElements,
    ): CosimaOption | undefined => {
        const xmlId = optionPresentElement.xmlId;
        if (!isDefined(xmlId)) {
            throw new Error(
                `OptionReducer: getNextOptionPresentSibling: optionPresentElement has no xml id! ${JSON.stringify(
                    optionPresentElement,
                )}`,
            );
        }
        const xmlIdParts = xmlId.split('_');
        const initialCounter = parseInt(xmlIdParts[xmlIdParts.length - 1], 10); // check if there is a number at the end
        const newXmlId = isNaN(initialCounter)
            ? `${xmlId}_2` // first element in sequence has no number suffix, so we just attach a 2
            : `${xmlIdParts.slice(0, -1).join('_')}_${initialCounter + 1}`; // for other elements, build prefix and increase counter
        const sibling = Object.values(functionalElements).find((fe) => fe.xmlId === newXmlId);
        if (isDefined(sibling) && !isCosimaOption(sibling)) {
            throw new Error(
                `OptionReducer: getNextOptionPresentSibling: found sibling which is not of type "option present"! ${JSON.stringify(
                    optionPresentElement,
                )} ${JSON.stringify(sibling)}`,
            );
        }
        return sibling;
    };

    export const shouldEmitEvent = (
        functionalElementChoices: Choices | undefined,
        currentSelectedChoice: Choice | undefined,
    ): boolean => {
        const previousSelectedChoice = isDefined(functionalElementChoices)
            ? Object.values(functionalElementChoices).find((c) => c?.selected)
            : undefined;
        const emitEvent = previousSelectedChoice?.xmlId !== currentSelectedChoice?.xmlId;

        return emitEvent;
    };

    export const getTranslationKeyForFormType = (
        formType: FormsConstants.FORMTYPE,
        infoClass = FormsConstants.INFOCLASS.UNKNOWN,
    ): string => {
        // astoria uses 837 for training and installation, this is only for text, so we just hard set it to INSTALLATION_REPORT
        const adaptedFormType =
            formType === FormsConstants.FORMTYPE.TRAININGCHECKLIST &&
            infoClass === FormsConstants.INFOCLASS.INSTALLATION
                ? FormsConstants.FORMTYPE.INSTALLATION_REPORT
                : formType;
        const formTypeKey = Object.keys(FormsConstants.FORMTYPE)[
            Object.values(FormsConstants.FORMTYPE).indexOf(adaptedFormType)
        ];

        return `F4M_FORM_TYPE_${formTypeKey}`;
    };
}
